import { get, set } from 'idb-keyval';

/**
 * Persistent string storage with IndexedDB primary + localStorage fallback.
 *
 * Useful for values that should survive page reloads but don't need to be
 * sent to the server (session ids, anonymous user ids, UI state, etc.).
 *
 * Falls back to localStorage when IndexedDB is unavailable or throws —
 * e.g. Safari private mode in older versions, partitioned third-party
 * contexts, or some browser extensions that block IDB.
 *
 * No-op (returns null / resolves) when neither store is available so
 * callers don't need to guard against SSR or storage being blocked.
 */

export async function getPersistentString(key: string): Promise<string | null> {
  // Prefer IndexedDB
  try {
    const value = await get<string>(key);
    if (typeof value === 'string') return value;
  } catch {
    // IDB unavailable — fall through
  }

  // Fallback: localStorage
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export async function setPersistentString(
  key: string,
  value: string,
): Promise<void> {
  // Try IndexedDB first
  try {
    await set(key, value);
    return;
  } catch {
    // Fall through to localStorage
  }

  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, value);
  } catch {
    // Both stores unavailable — give up silently
  }
}
